// Canonical display provider — AgDR-0039 (density presets + font size scaling),
// text range amended by AgDR-0288 §3. Two user-controlled axes: density
// (compact / comfortable / cozy) and font scale (M / L / XL / XXL). Both persist
// to localStorage and toggle CSS custom properties consumed by the design-tokens
// module (AgDR-0036).
import { type ReactNode, useEffect, useState } from 'react';

import { type Density, DisplayContext, type FontScale } from './display-context';

// Multiples of the browser's base size, applied by theme.css as
// `html { font-size: calc(100% * var(--font-scale)) }`: 16 / 20 / 24 / 32 px when
// the browser is at 16 px. M is the floor and XXL the 200 % of WCAG 1.4.4.
const FONT_SCALE_FACTOR: Record<FontScale, number> = { M: 1, L: 1.25, XL: 1.5, XXL: 2 };
const DENSITY_SPACING: Record<Density, number> = { compact: 0.75, comfortable: 1, cozy: 1.25 };

export function DisplayProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [density, setDensityState] = useState<Density>(() => {
    if (typeof localStorage === 'undefined') return 'comfortable';
    const v = localStorage.getItem('display-density');
    return v === 'compact' || v === 'comfortable' || v === 'cozy' ? v : 'comfortable';
  });
  const [fontScale, setFontScaleState] = useState<FontScale>(() => {
    if (typeof localStorage === 'undefined') return 'M';
    const v = localStorage.getItem('display-font-scale');
    // Any other value, including the former `S` step, falls back to 100 %.
    return v === 'M' || v === 'L' || v === 'XL' || v === 'XXL' ? v : 'M';
  });

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--font-scale', String(FONT_SCALE_FACTOR[fontScale]));
    root.style.setProperty('--density-spacing', String(DENSITY_SPACING[density]));
    root.dataset.density = density;
    root.dataset.fontScale = fontScale;
  }, [density, fontScale]);

  const setDensity = (d: Density) => {
    setDensityState(d);
    localStorage.setItem('display-density', d);
  };
  const setFontScale = (s: FontScale) => {
    setFontScaleState(s);
    localStorage.setItem('display-font-scale', s);
  };

  return (
    <DisplayContext.Provider value={{ density, fontScale, setDensity, setFontScale }}>
      {children}
    </DisplayContext.Provider>
  );
}
